/**
 * Which "area" a route belongs to, for activity telemetry.
 *
 * Pure on purpose: the hook that calls it (lib/useAreaTracking) pulls in React
 * and the BFF fetch client, and this is the part worth testing on its own.
 */

import { areaFromPath, moduleBaseForPath } from './apps';

/**
 * Structurally compatible with AppShell's `NavEntry` union without importing
 * it: an entity depending on a widget is the wrong direction, and only the
 * path and the label matter here. Section headings have no `path`.
 */
export interface AreaNavEntry {
  path?: string;
  label: string;
  children?: { path: string; label: string }[];
}

export const stripTrailingSlash = (p: string) =>
  p.length > 1 && p.endsWith('/') ? p.replace(/\/+$/, '') : p;

/**
 * How specifically a nav path claims this location, or -1 for no claim.
 *
 * Two rules earn their keep. Trailing slashes are normalized, because every
 * module builds its root item as `${basePath}/` ('/ops/') while the router
 * reports '/ops' - that one mismatch is what made every module's landing page
 * log 'Unknown' (ADO #1558). And the module root matches EXACTLY rather than by
 * prefix: it prefixes every route in the module, so a prefix match would let
 * 'Dashboard' swallow every page with no nav item of its own.
 */
function claimLength(navPath: string, path: string, base: string): number {
  const p = stripTrailingSlash(navPath);
  if (p === base) return path === p ? p.length : -1;
  return path === p || path.startsWith(p + '/') ? p.length : -1;
}

/**
 * The nav label for a location, falling back to a name derived from the path.
 *
 * The fallback is the point: nav items are role-gated and no module names every
 * one of its routes, so "no nav item matched" is normal and used to be recorded
 * as the literal string 'Unknown'.
 */
export function areaForLocation(pathname: string, navItems: readonly AreaNavEntry[]): string {
  const path = stripTrailingSlash(pathname);
  const base = moduleBaseForPath(path);
  let area = '';
  let best = -1;
  for (const item of navItems) {
    if (!item.path) continue; // section heading
    const len = Math.max(
      claimLength(item.path, path, base),
      ...(item.children ?? []).map((c) => claimLength(c.path, path, base)),
    );
    if (len > best) {
      best = len;
      area = item.label;
    }
  }
  return best >= 0 ? area : areaFromPath(path);
}
